import { useEffect, useState, useCallback } from 'react';

export type InsuranceBffState = 'idle' | 'loading' | 'passed' | 'passed-no-insurance' | 'failed';

interface UseInsuranceEsiArgs {
  enabled: boolean; // experiments['se-20']
  isInsuranceAvailable: boolean;
  productName: string;
}

interface UseInsuranceEsiResult {
  insuranceBffState: InsuranceBffState;
  hasInsuranceBeenSelected: boolean;
  shouldInterceptAtb: boolean;
  dispatchAtbIntercept: () => void;
  isAtbDisabled: boolean;
}

export function useInsuranceEsi({
  enabled,
  isInsuranceAvailable,
  productName,
}: UseInsuranceEsiArgs): UseInsuranceEsiResult {
  const [insuranceBffState, setInsuranceBffState] = useState<InsuranceBffState>('idle');
  const [hasInsuranceBeenSelected, setHasInsuranceBeenSelected] = useState(false);

  useEffect(() => {
    const handleGoToBasket = () => {
      window.location.href = '/basket';
    };

    window.addEventListener('insurance-fe:go-to-basket', handleGoToBasket);

    return () => {
      window.removeEventListener('insurance-fe:go-to-basket', handleGoToBasket);
    };
  }, []);

  /**
   * Initialise BFF state
   */
  useEffect(() => {
    if (enabled && isInsuranceAvailable) {
      setInsuranceBffState('loading');
    }
  }, [enabled, isInsuranceAvailable]);

  /**
   * BFF lifecycle events
   */
  useEffect(() => {
    if (!enabled || !isInsuranceAvailable) {
      return () => {};
    }

    const onPassed = () => setInsuranceBffState('passed');
    const onNoInsurance = () => setInsuranceBffState('passed-no-insurance');
    const onFailed = () => setInsuranceBffState('failed');

    window.addEventListener('insurance-fe:bff-api-passed', onPassed);
    window.addEventListener('insurance-fe:bff-api-no-insurance', onNoInsurance);
    window.addEventListener('insurance-fe:bff-api-failed', onFailed);

    return () => {
      window.removeEventListener('insurance-fe:bff-api-passed', onPassed);
      window.removeEventListener('insurance-fe:bff-api-no-insurance', onNoInsurance);
      window.removeEventListener('insurance-fe:bff-api-failed', onFailed);
    };
  }, [enabled, isInsuranceAvailable]);

  /**
   * Insurance selection event (from ESI)
   */
  useEffect(() => {
    const onInsuranceUpdated = () => {
      setHasInsuranceBeenSelected(true);
    };

    window.addEventListener('insurance-fe:insurance-updated', onInsuranceUpdated);

    return () => {
      window.removeEventListener('insurance-fe:insurance-updated', onInsuranceUpdated);
    };
  }, []);

  /**
   * Should we intercept Add To Basket?
   */
  const shouldInterceptAtb =
    enabled && isInsuranceAvailable && !hasInsuranceBeenSelected && insuranceBffState === 'passed';

  /**
   * Dispatch intercept event
   */
  const dispatchAtbIntercept = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('product-fe:atb-clicked', {
        detail: { productName },
      }),
    );
  }, [productName]);

  const isAtbDisabled = enabled && isInsuranceAvailable && insuranceBffState === 'loading';

  return {
    insuranceBffState,
    hasInsuranceBeenSelected,
    shouldInterceptAtb,
    dispatchAtbIntercept,
    isAtbDisabled,
  };
}
